import { useEffect, useState } from 'react';
import { api, type DeploymentStatusSnapshot } from './api';

export interface DeploymentStatuses {
  histories: Record<string, DeploymentStatusSnapshot>;
  unavailable: string[];
  denied: string[];
}
const empty = (): DeploymentStatuses => ({ histories: {}, unavailable: [], denied: [] });

/** One request loop per catalog/detail, with no persisted authorization or cross-user cache. */
export function useDeploymentStatuses(repos: string[], enabled = true) {
  const key = [...new Set(repos)].sort().join(',');
  const [result, setResult] = useState<DeploymentStatuses>(empty);
  useEffect(() => {
    setResult(empty());
    if (!enabled || !key) return;
    const requested = key.split(',');
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let failures = 0;
    let running = false;
    async function refresh() {
      clearTimeout(timer);
      if (cancelled || running || document.hidden) return;
      running = true;
      let interval = 30_000;
      try {
        const histories: Record<string, DeploymentStatusSnapshot> = {};
        const unavailable: string[] = [];
        // Bound batch size server-side and client-side; no per-row fallback on errors.
        for (let offset = 0; offset < requested.length; offset += 100) {
          const batch = await api.deploymentStatuses(requested.slice(offset, offset + 100));
          Object.assign(histories, batch.histories);
          unavailable.push(...batch.unavailable);
        }
        if (cancelled) return;
        setResult({ histories, unavailable, denied: requested.filter(repo => !histories[repo] && !unavailable.includes(repo)) });
        const active = Object.values(histories).some(history => history.runs.some(run => run.status === 'queued' || run.status === 'in_progress'));
        failures = 0;
        interval = active ? 10_000 : 30_000;
      } catch {
        if (cancelled) return;
        // Authorization failure must never retain previously fetched shared facts.
        setResult({ histories: {}, unavailable: requested, denied: [] });
        interval = Math.min(120_000, 30_000 * 2 ** failures++);
      } finally {
        running = false;
        if (!cancelled && !document.hidden) timer = setTimeout(refresh, interval);
      }
    }
    const visibility = () => { clearTimeout(timer); if (!document.hidden) void refresh(); };
    document.addEventListener('visibilitychange', visibility);
    void refresh();
    return () => { cancelled = true; clearTimeout(timer); document.removeEventListener('visibilitychange', visibility); };
  }, [key, enabled]);
  return result;
}
